import { useTranslation } from 'react-i18next';

import { Link } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';

import { useWhoami } from '@/features/auth/hooks/use-whoami-query';
import { useGetBonusesByUserId } from '@/features/bonus/use-get-bonuses-by-user-id';
import { useGetUserById } from '@/features/user/hooks/use-get-user-by-id';

import { BonusChip } from '@/entities/bonus/bonus-chip';

import { cn } from '@/lib/shadcn/lib/utils';

interface SideMenuUserButtonProps {
  onClose?: () => void;
  className?: string;
}

export function SideMenuUserButton({ onClose, className }: SideMenuUserButtonProps) {
  const { t } = useTranslation();

  const { data: whoamiData } = useWhoami({
    params: {},
  });

  const userId = whoamiData?.auth.userId ?? '';
  const roles = whoamiData?.auth.roles ?? [];
  const isAuthenticated = !!userId && !roles.includes('unconfirmed-client');

  const { data: userData } = useGetUserById({
    params: { userId },
    enabled: isAuthenticated,
  });

  const { data: bonusData } = useGetBonusesByUserId({
    params: {},
    enabled: isAuthenticated,
  });

  const userFullName = userData?.user
    ? [userData.user.firstName, userData.user.lastName].filter(Boolean).join(' ') ||
      userData.user.firstName ||
      t('landing.header.profile')
    : t('landing.header.profile');

  const userBonuses = Math.trunc(bonusData?.bonuses ?? 0);

  if (isAuthenticated) {
    return (
      <Link
        to="/profile"
        onClick={onClose}
        className={cn(
          'flex min-h-[54px] w-full shrink-0 cursor-pointer items-center gap-2.5 rounded-medium bg-slate-100 p-1.5 pr-3 text-left transition-colors hover:bg-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 active:bg-slate-250',
          className,
        )}
      >
        <span className="flex size-10 shrink-0 items-center justify-center rounded-medium bg-teal-100">
          <svg aria-hidden="true" className="size-[22px]" viewBox="0 0 24 24">
            <circle cx="12" cy="8" r="4" className="fill-teal-500" />
            <ellipse cx="12" cy="18" rx="7" ry="4" className="fill-teal-200" />
          </svg>
        </span>
        <span className="flex min-w-0 flex-1 flex-col items-start justify-center gap-1 overflow-hidden">
          <span
            className="block w-full min-w-0 truncate text-[15px] font-semibold leading-[1.1] tracking-[-0.02em] text-slate-500"
            title={userFullName}
          >
            {userFullName}
          </span>
          <BonusChip bonuses={userBonuses} size="sm" rounded="full" />
        </span>
        <ChevronRight className="size-5 shrink-0 text-slate-300" />
      </Link>
    );
  }

  return (
    <Link
      to="/profile"
      aria-label={t('landing.header.profile')}
      onClick={onClose}
      className={cn(
        'landing-profile-link flex min-h-[54px] w-full shrink-0 cursor-pointer items-center justify-center gap-2 rounded-medium border border-teal-50 bg-teal-50 text-[16px] font-semibold tracking-[-0.02em] text-teal-500 transition-colors hover:bg-teal-100/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 active:bg-teal-200',
        className,
      )}
    >
      <svg
        aria-hidden="true"
        className="size-[22px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      >
        <circle cx="12" cy="8" r="4" />
        <path d="M4.5 20c.9-3.6 3.9-6 7.5-6s6.6 2.4 7.5 6" />
      </svg>
      <span className="truncate">{t('landing.header.signInProfile')}</span>
    </Link>
  );
}
